import type { ConnectedProfile } from "../../../types/customer";
import type { TikTokCreatorInfo } from "../services/tiktokAuthentication";
import type { TikTokOptions } from "../types";
import { isTikTokConfigured } from "../config/tiktokEnv";

export const TIKTOK_MUSIC_USAGE_URL =
  "https://www.tiktok.com/legal/page/global/music-usage-confirmation/en";
export const TIKTOK_BRANDED_CONTENT_POLICY_URL =
  "https://www.tiktok.com/legal/page/global/bc-policy/en";

export const TIKTOK_DISCLOSURE_REQUIRED_MESSAGE =
  "You need to indicate if your content promotes yourself, a third party, or both.";
export const TIKTOK_BRANDED_PRIVATE_MESSAGE =
  "Branded content visibility cannot be set to private.";
export const TIKTOK_PROCESSING_MESSAGE =
  "TikTok is processing your video. It can take a few minutes to appear on your profile.";
export const TIKTOK_INBOX_MESSAGE =
  "Sent to your TikTok inbox — open the TikTok app to finish posting.";
export const TIKTOK_VIDEO_REQUIRED_MESSAGE = "TikTok posts need a video.";
export const TIKTOK_SCHEDULE_INCOMPLETE_MESSAGE =
  "This scheduled TikTok post won't go out until its TikTok settings are complete.";
export const TIKTOK_INBOX_APPROVE_TOOLTIP = "Already sent to your TikTok inbox";
export const TIKTOK_CONSENT_REQUIRED_MESSAGE =
  "TikTok posts need TikTok's posting settings confirmed first. Open the post in Post Manager (Review Posts), choose the TikTok settings, and publish from there.";

export const TIKTOK_PRIVACY_LABELS: Record<string, string> = {
  PUBLIC_TO_EVERYONE: "Everyone",
  MUTUAL_FOLLOW_FRIENDS: "Friends",
  FOLLOWER_OF_CREATOR: "Followers",
  SELF_ONLY: "Only me",
};

/** Post platform keys that go through the TikTok composer. */
export function isTikTokPlatform(platform?: string | null): boolean {
  const upper = String(platform ?? "").trim().toUpperCase();
  return upper === "TIKTOK_REELS" || upper === "TIKTOK";
}

/** Everything off / unchosen: TikTok forbids pre-selected interaction or privacy values. */
export function defaultTikTokOptions(): TikTokOptions {
  return {
    mode: "DIRECT_POST",
    allow_comment: false,
    allow_duet: false,
    allow_stitch: false,
    commercial_content: false,
    brand_organic: false,
    brand_content: false,
    is_aigc: false,
  };
}

export function resolveTikTokOptions(raw?: TikTokOptions | null): TikTokOptions {
  return { ...defaultTikTokOptions(), ...(raw ?? {}) };
}

/**
 * Why this post can't be published/scheduled yet (empty = ready).
 *
 * `creatorInfo === null` means "not available" (loading, reconnect, error) and
 * blocks. `undefined` means "caller has no creator info" and skips the checks
 * that need it (used for the cheap guards in ReviewPost, which can't see it).
 * `hasVideo === false` adds the "needs a video" problem (undefined skips it).
 */
export function missingTikTokRequiredFields(
  options: TikTokOptions | undefined | null,
  creatorInfo: TikTokCreatorInfo | null | undefined,
  videoDurationSec?: number | null,
  hasVideo?: boolean
): string[] {
  const opts = resolveTikTokOptions(options);
  const problems: string[] = [];

  if (hasVideo === false) {
    problems.push(TIKTOK_VIDEO_REQUIRED_MESSAGE);
  }

  if (creatorInfo === null) {
    problems.push("TikTok account details aren't available yet.");
  }

  if (opts.mode !== "INBOX_DRAFT") {
    if (!opts.privacy_level) {
      problems.push("Choose who can view this video on TikTok.");
    } else if (
      creatorInfo &&
      !(creatorInfo.privacy_level_options || []).includes(opts.privacy_level)
    ) {
      problems.push("The selected privacy option isn't available for this TikTok account.");
    }
    if (opts.commercial_content && !opts.brand_organic && !opts.brand_content) {
      problems.push(TIKTOK_DISCLOSURE_REQUIRED_MESSAGE);
    }
    if (opts.brand_content && opts.privacy_level === "SELF_ONLY") {
      problems.push(TIKTOK_BRANDED_PRIVATE_MESSAGE);
    }
  }

  const max = creatorInfo?.max_video_post_duration_sec;
  if (
    typeof max === "number" &&
    max > 0 &&
    typeof videoDurationSec === "number" &&
    videoDurationSec > max
  ) {
    problems.push(
      `This video is ${Math.round(videoDurationSec)}s long; TikTok allows up to ${max}s for this account.`
    );
  }
  return problems;
}

/** The "how will TikTok label this" notice for the disclosure section. */
export function tiktokLabelNotice(options: TikTokOptions): string | null {
  if (!options.commercial_content) return null;
  if (options.brand_content) return "Your video will be labeled as 'Paid partnership'";
  if (options.brand_organic) return "Your video will be labeled as 'Promotional content'";
  return null;
}

export function usesBrandedContentPolicy(options?: TikTokOptions | null): boolean {
  const opts = resolveTikTokOptions(options);
  return opts.mode !== "INBOX_DRAFT" && !!opts.commercial_content && !!opts.brand_content;
}

export function isRealTikTokOpenId(id?: string | null): boolean {
  const value = String(id ?? "").trim();
  return value !== "" && !value.toLowerCase().startsWith("default_");
}

/** The customer's connected TikTok open_ids, in connection order. */
export function getTikTokProfileIds(connectedProfiles?: ConnectedProfile[] | null): string[] {
  return (Array.isArray(connectedProfiles) ? connectedProfiles : [])
    .filter((p) => String(p?.platform ?? "").toUpperCase() === "TIKTOK")
    .flatMap((p) => (p.details ?? []).map((d) => d.id))
    .filter((id) => isRealTikTokOpenId(id));
}

/**
 * The open_id a post must publish to: its own business_profile_id when that is a
 * connected open_id, otherwise the customer's first connected TikTok profile.
 */
export function resolveTikTokProfileId(
  businessProfileId: string | undefined | null,
  connectedProfiles?: ConnectedProfile[] | null
): string | undefined {
  const ids = getTikTokProfileIds(connectedProfiles);
  if (businessProfileId && ids.includes(businessProfileId)) return businessProfileId;
  return ids[0];
}

/** Hide TikTok in pickers unless the app is configured AND this customer connected an account. */
export function isTikTokPostingAvailable(connectedProfiles?: ConnectedProfile[] | null): boolean {
  return isTikTokConfigured() && getTikTokProfileIds(connectedProfiles).length > 0;
}

/** Drop any stored consent: it covered the settings as they were, not as they are now. */
export function stripTikTokConsent(options: TikTokOptions | undefined | null): TikTokOptions {
  const rest = resolveTikTokOptions(options);
  delete rest.consent_at;
  delete rest.consent_by;
  return rest;
}

/**
 * The payload the backend accepts once consent is set. The backend validates the
 * whole object at that point, so leftovers from the other mode must not travel:
 * an inbox draft carries only `mode` (TikTok's inbox endpoint takes no post_info),
 * a direct post carries no disclosure flags when disclosure is off.
 */
export function sanitizeTikTokOptions(options: TikTokOptions | undefined | null): TikTokOptions {
  const opts = resolveTikTokOptions(options);
  if (opts.mode === "INBOX_DRAFT") return { mode: "INBOX_DRAFT" };
  const out: TikTokOptions = { ...opts, mode: "DIRECT_POST" };
  if (!out.commercial_content) {
    out.brand_organic = false;
    out.brand_content = false;
  }
  return out;
}

export function stampTikTokConsent(
  options: TikTokOptions | undefined | null,
  userEmail: string
): TikTokOptions {
  return {
    ...sanitizeTikTokOptions(stripTikTokConsent(options)),
    consent_at: new Date().toISOString(),
    consent_by: userEmail,
  };
}

export type TikTokPublishOutcome =
  | { kind: "done"; status: "PUBLISHED" | "SENT_TO_TIKTOK" }
  | { kind: "failed"; error: string }
  | { kind: "processing" };

/**
 * Read a TikTok row's state while polling after Publish. A missing
 * platform_post_id is normal (SELF_ONLY posts have none; TikTok is still
 * processing otherwise), so it is never an error by itself.
 */
export function evaluateTikTokPublishState(post: {
  status?: string;
  platform_post_id?: string | null;
  platform_response?: any;
}): TikTokPublishOutcome {
  const status = String(post?.status ?? "").toUpperCase();
  if (status === "SENT_TO_TIKTOK") return { kind: "done", status: "SENT_TO_TIKTOK" };
  if (status === "PUBLISHED") return { kind: "done", status: "PUBLISHED" };
  const pr = post?.platform_response;
  if (status === "FAILED" || (pr && pr.error)) {
    const raw = pr?.error;
    const msg =
      typeof raw === "string" ? raw : raw ? JSON.stringify(raw) : "TikTok could not publish this video.";
    return { kind: "failed", error: msg };
  }
  return { kind: "processing" };
}
